import { useMutation } from '@tanstack/react-query'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import TextButton from 'LuxKit/Button/TextButton'
import Modal from 'LuxKit/Modal/Modal'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import { SET_SOCIETE_REQUESTS_INTERACTIONS } from 'actions/actionConstants'
import { fetchAccountAgentHub } from 'actions/AgentHubActions'
import requestPostDismissSocieteMembership from 'agentHub/api/requestPostDismissSocieteMembership'
import { agentHubOrSubBrandConfig } from 'agentHub/constants'
import Group from 'components/utils/Group'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import { useAppDispatch } from 'hooks/reduxHooks'
import { useCallback, useState } from 'react'
import { useIntl } from 'react-intl'
import SocieteCreateNewForm from './SocieteCreateNewForm'
import SocieteLinkExistingForm from './SocieteLinkExistingForm'
import SocieteRewardsBenefits from './SocieteRewardsBenefits'

type Step = 'linkExisting' | 'createNew' | 'success' | 'emailSent'

interface Props {
  dismissType: 'login' | 'checkout'
}

function SocietePromptModal({ dismissType }: Props) {
  const intl = useIntl()
  const dispatch = useAppDispatch()
  const modalContext = useModalElementContext()
  const [step, setStep] = useState<Step>('linkExisting')
  const [createNewPrefillEmail, setCreateNewPrefillEmail] = useState<string>('')

  const dismissMutation = useMutation({
    mutationFn: async() => {
      return requestPostDismissSocieteMembership({ dismissType })
    },
    onSuccess: () => {
      dispatch({
        type: SET_SOCIETE_REQUESTS_INTERACTIONS,
        interactions: { [dismissType]: { deniedAt: new Date().toISOString() } },
      })
      modalContext.resolve()
    },
  })

  const handleDismiss = useCallback(() => {
    dismissMutation.mutate()
  }, [dismissMutation])

  const dispatchEngaged = useCallback(() => {
    dispatch({
      type: SET_SOCIETE_REQUESTS_INTERACTIONS,
      interactions: { engaged: { engagedAt: new Date().toISOString() } },
    })
  }, [dispatch])

  const handleLinkSuccess = useCallback(() => {
    dispatchEngaged()
    dispatch(fetchAccountAgentHub())
    setStep('success')
  }, [dispatch, dispatchEngaged])

  const handleEmailSent = useCallback(() => {
    dispatchEngaged()
    setStep('emailSent')
  }, [dispatchEngaged])

  const handleCreateSuccess = useCallback(() => {
    dispatchEngaged()
    dispatch(fetchAccountAgentHub())
    setStep('success')
  }, [dispatch, dispatchEngaged])

  const handleCreateNewFromLink = useCallback((prefillEmail?: string) => {
    setCreateNewPrefillEmail(prefillEmail ?? '')
    setStep('createNew')
  }, [])

  const handleBack = useCallback(() => {
    setStep('linkExisting')
    setCreateNewPrefillEmail('')
  }, [])

  const isCompactStep = step === 'success' || step === 'emailSent'

  const title = (() => {
    switch (step) {
      case 'linkExisting':
        return intl.formatMessage({
          id: 'PcrS0893gfJkHZ7stiv6',
          defaultMessage: 'Société Rewards',
        })
      case 'createNew':
        return intl.formatMessage({
          id: 'epFSOjJLHQrQAqm73lsO',
          defaultMessage: 'Create new account',
        })
      case 'success':
        return intl.formatMessage({
          id: '9utiBeDIHWB0aQWn2cfy',
          defaultMessage: 'Account linked!',
        })
      case 'emailSent':
        return intl.formatMessage({
          id: 'pSeLCiEWgn1DYXI2u0fK',
          defaultMessage: 'Check your email',
        })
    }
  })()

  return (
    <Modal
      size={isCompactStep ? 'S' : 'L'}
      title={title}
      onBack={step === 'createNew' ? handleBack : undefined}
    >
      {step === 'linkExisting' && (
        <Group direction="vertical" gap={20}>
          <SocieteRewardsBenefits />
          <SocieteLinkExistingForm
            onSuccess={handleLinkSuccess}
            onEmailSent={handleEmailSent}
            onCreateNew={handleCreateNewFromLink}
          />
          <TextButton
            fit="mobile-full-width"
            kind="tertiary"
            size="medium"
            onClick={handleDismiss}
            disabled={dismissMutation.isLoading}
          >
            {intl.formatMessage({
              id: '1PLnUWvWVulEdzKPRvGa',
              defaultMessage: 'Maybe later',
            })}
          </TextButton>
        </Group>
      )}

      {step === 'createNew' && (
        <SocieteCreateNewForm
          onSuccess={handleCreateSuccess}
          onEmailSent={handleEmailSent}
          defaultEmail={createNewPrefillEmail}
        />
      )}

      {step === 'success' && (
        <Group direction="vertical" gap={20}>
          <BodyText variant="medium">
            {intl.formatMessage({
              id: 'PrGKaNMXVfTowyAbaraa',
              defaultMessage: 'Your Société account has been successfully linked. You will now earn loyalty points on purchases made through {name}.',
            }, { name: agentHubOrSubBrandConfig.displayShortName })}
          </BodyText>
          <TextButton
            fit="mobile-full-width"
            kind="primary"
            size="large"
            onClick={() => modalContext.resolve()}
          >
            {intl.formatMessage({
              id: 'KaCckgihtRjpCDbjsVl2',
              defaultMessage: 'Done',
            })}
          </TextButton>
        </Group>
      )}

      {step === 'emailSent' && (
        <Group direction="vertical" gap={20}>
          {dismissMutation.isError && (
            <MessageBanner kind="critical">
              {intl.formatMessage({
                id: 'x0P7616zAoqG827cDUtc',
                defaultMessage: 'Something went wrong. Please try again.',
              })}
            </MessageBanner>
          )}
          <BodyText variant="medium">
            {intl.formatMessage({
              id: 'cexgwopHsZ27OfZqSkiO',
              defaultMessage: 'We have sent a confirmation email to verify your Société account. Please check your inbox and click the link to complete the linking process.',
            })}
          </BodyText>
          <Heading variant="heading6">
            {intl.formatMessage({
              id: 'xms-0Zetumb2-NPsrbAD',
              defaultMessage: 'You can close this dialog and complete the verification later.',
            })}
          </Heading>
          <TextButton
            fit="mobile-full-width"
            kind="primary"
            size="large"
            onClick={() => modalContext.resolve()}
          >
            {intl.formatMessage({
              id: 'ZnBxorrCthxQAyEION0S',
              defaultMessage: 'Close',
            })}
          </TextButton>
        </Group>
      )}
    </Modal>
  )
}

export default SocietePromptModal
